import React from 'react';

// Pricing Page Component
const PricingPage = ({ navigate }) => {
    const packages = [
        {
            id: 'basic',
            name: 'Delegate Starter Pack',
            price: '$---',
            description: 'Ideal for new delegates. Includes conference access, basic materials, and opening/closing ceremonies.',
            features: [
                'Full conference access',
                'Committee participation',
                'Basic delegate kit',
                'Opening & Closing Ceremonies',
                'Coffee break beverages',
            ],
            flair: null,
            buttonText: 'View Details',
            bgColor: 'bg-white',
            textColor: 'text-gray-800',
            borderColor: 'border-gray-200',
            buttonBg: 'bg-blue-600',
            buttonHover: 'hover:bg-blue-700',
            buttonTextCol: 'text-white'
        },
        {
            id: 'standard',
            name: 'Diplomat\'s Choice',
            price: '$---',
            description: 'Our most popular package. Everything in Starter, plus social events and enhanced materials.',
            features: [
                'All Starter Pack features',
                'Exclusive social events',
                'Premium delegate kit',
                'Networking opportunities',
                'Certificate of participation',
                'Light lunch buffet access',
            ],
            flair: 'Popular',
            flairColor: 'bg-green-500',
            flairText: 'text-white',
            buttonText: 'View Details',
            bgColor: 'bg-blue-50',
            textColor: 'text-blue-800',
            borderColor: 'border-blue-300',
            buttonBg: 'bg-blue-700',
            buttonHover: 'hover:bg-blue-800',
            buttonTextCol: 'text-white'
        },
        {
            id: 'premium',
            name: 'Global Leader Tier',
            price: '$---',
            description: 'For the aspiring leader. All Standard features, plus mentorship and priority access.',
            features: [
                'All Diplomat\'s Choice features',
                'Dedicated mentor session',
                'Priority committee selection',
                'Exclusive workshop access TBA',
            ],
            flair: null,
            flairColor: 'bg-yellow-500',
            flairText: 'text-gray-900',
            buttonText: 'View Details',
            bgColor: 'bg-white',
            textColor: 'text-gray-800',
            borderColor: 'border-gray-200',
            buttonBg: 'bg-blue-600',
            buttonHover: 'hover:bg-blue-700',
            buttonTextCol: 'text-white'
        },
        {
            id: 'vip',
            name: 'Elite Delegate Experience',
            price: '$---',
            description: 'The ultimate MUN experience. Everything in Global Leader, plus personalized support and premium accommodation options.',
            features: [
                'All Global Leader features',
                'Personalized logistical support',
                'Premium accommodation options', // Note: accommodation costs are separate
                'Exclusive pre-conference briefing',
            ],
            flair: null,
            flairColor: 'bg-red-500',
            flairText: 'text-white',
            buttonText: 'View Details',
            bgColor: 'bg-white',
            textColor: 'text-gray-800',
            borderColor: 'border-gray-200',
            buttonBg: 'bg-blue-600',
            buttonHover: 'hover:bg-blue-700',
            buttonTextCol: 'text-white'
        },
    ];

    return (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-4xl font-bold text-blue-800 mb-12 text-center animate-fade-in">Choose Your ThessMUN Experience</h2>
            <p className="text-xl text-gray-700 text-center mb-10 max-w-3xl mx-auto">
                Select the package that best fits your goals and budget. Each tier offers a unique set of benefits designed to enhance your Model United Nations journey.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {packages.map((pkg) => (
                    <div key={pkg.id} className={`relative ${pkg.bgColor} ${pkg.textColor} border-4 ${pkg.borderColor} rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col justify-between`}>
                        {pkg.flair && (
                            <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold uppercase tracking-wide ${pkg.flairColor} ${pkg.flairText} shadow-md`}>
                                {pkg.flair}
                            </div>
                        )}
                        <div className="p-8 flex-grow">
                            <h3 className="text-3xl font-bold mb-4 text-center">{pkg.name}</h3>
                            <p className="text-5xl font-extrabold text-center mb-6 text-blue-700">{pkg.price}</p>
                            <p className="text-center text-gray-600 mb-6">{pkg.description}</p>
                            <ul className="list-none space-y-3 mb-8">
                                {pkg.features.map((feature, index) => (
                                    <li key={index} className="flex items-center text-gray-700">
                                        <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                        </svg>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-8 pt-0">
                            <button
                                onClick={() => navigate(`package-details-${pkg.id}`)}
                                className={`w-full ${pkg.buttonBg} ${pkg.buttonTextCol} font-bold py-3 px-6 rounded-full shadow-lg ${pkg.buttonHover} transition-colors duration-300 transform hover:scale-105`}
                            >
                                {pkg.buttonText}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

// Package Detail Page Component
const PackageDetailPage = ({ packageId, navigate }) => {
    // In a real app, you'd fetch this from a database or a more robust data source
    const packagesData = {
        'basic': {
            name: 'Delegate Starter Pack',
            fullDescription: 'The Delegate Starter Pack is designed for first-time MUN participants or those looking for a fundamental conference experience. It provides all the essentials to engage in committee sessions and be part of the opening and closing ceremonies. This package is perfect for delegates who want to focus purely on the academic aspects of the conference.',
            inclusions: [
                'Access to all committee sessions (DISEC, SOCHUM, etc.)',
                'Official delegate badge and lanyard',
                'Basic stationery kit (notepad, pen)',
                'Participation in Opening and Closing Ceremonies',
                'Access to general conference areas',
                'Online access to committee background guides',
            ],
            idealFor: 'New delegates, students on a budget, those primarily interested in debate.',
            note: 'Meals, accommodation, and social events are not included.',
            price: '$150'
        },
        'standard': {
            name: 'Diplomat\'s Choice',
            fullDescription: 'The Diplomat\'s Choice is our most popular package, offering a balanced and enriching experience. Beyond the core conference activities, it includes access to exclusive social events, allowing delegates to network and unwind in a more informal setting. This package is ideal for delegates seeking a comprehensive MUN experience with added social benefits.',
            inclusions: [
                'All features of the Delegate Starter Pack',
                'Invitation to the Delegate Social Night',
                'Access to the Cultural Exchange Mixer',
                'Premium delegate kit (includes a ThessMUN branded folder, water bottle, and more)',
                'Professional networking sessions with Secretariat members',
                'Official certificate of participation',
                'Light refreshments during breaks',
            ],
            idealFor: 'Delegates seeking a full academic and social experience, those wanting to network.',
            note: 'Accommodation and some specialized workshops are not included.',
            price: '$250'
        },
        'premium': {
            name: 'Global Leader Tier',
            fullDescription: 'The Global Leader Tier is crafted for ambitious delegates aiming to maximize their learning and leadership development. This package provides all the benefits of the Diplomat\'s Choice, with added opportunities for personalized mentorship, priority access to committees, and specialized workshops designed to hone advanced diplomatic skills. It\'s an investment in your future as a global leader.',
            inclusions: [
                'All features of the Diplomat\'s Choice package',
                'One-on-one mentorship session with an experienced Secretariat member or faculty advisor',
                'Priority access to select committee assignments (based on application)',
                'Exclusive invitation to advanced diplomatic workshops (e.g., crisis simulation, advanced resolution writing)',
                'Invitation to the formal Gala Dinner',
                'Recognition in the official conference yearbook',
                'Dedicated support hotline during the conference',
            ],
            idealFor: 'Experienced delegates, aspiring leaders, students focused on skill enhancement.',
            note: 'Accommodation is not included. Priority committee selection is subject to availability and application review.',
            price: '$400'
        },
        'vip': {
            name: 'Elite Delegate Experience',
            fullDescription: 'The Elite Delegate Experience is the pinnacle of ThessMUN participation, offering unparalleled comfort, support, and exclusive access. This package combines all the benefits of the Global Leader Tier with personalized logistical assistance, premium accommodation options, and VIP access to ensure a seamless and unforgettable conference journey. It\'s designed for delegates who desire the utmost convenience and a truly premium experience.',
            inclusions: [
                'All features of the Global Leader Tier',
                'Personalized pre-conference logistical support (travel, visa assistance)',
                'Access to premium hotel accommodation options (additional cost applies for hotel booking)',
                'Exclusive pre-conference briefing with the Secretary-General',
                'Access to the VIP delegate lounge with complimentary snacks and beverages',
                'Private transportation to and from official conference venues',
                'Priority registration and check-in',
                'Customized delegate gift package',
            ],
            idealFor: 'Delegates seeking maximum comfort, convenience, and exclusive access.',
            note: 'Accommodation costs are separate and will be arranged based on preference. Limited availability.',
            price: '$600'
        },
    };

    const pkg = packagesData[packageId];

    if (!pkg) {
        return (
            <section className="container mx-auto px-6 py-16 text-gray-800 text-center">
                <h2 className="text-4xl font-bold text-blue-800 mb-8">Package Not Found</h2>
                <p className="text-lg mb-6">The package you are looking for does not exist.</p>
                <button onClick={() => navigate('pricing')} className="bg-blue-700 text-white font-bold py-3 px-6 rounded-full hover:bg-blue-800 transition-colors duration-300">
                    Back to Pricing
                </button>
            </section>
        );
    }

    return (
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-4xl font-bold text-blue-800 mb-8 text-center animate-fade-in">{pkg.name} Details</h2>
            <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl mx-auto animate-fade-in delay-100">
                <p className="text-blue-700 text-4xl font-extrabold mb-6 text-center">{pkg.price}</p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">{pkg.fullDescription}</p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="text-2xl font-semibold text-blue-700 mb-4">What's Included:</h3>
                        <ul className="list-none space-y-3">
                            {pkg.inclusions.map((item, index) => (
                                <li key={index} className="flex items-start text-gray-700">
                                    <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                                    </svg>
                                    <span className="flex-grow">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-2xl font-semibold text-blue-700 mb-4">Who It's For:</h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">{pkg.idealFor}</p>
                        {pkg.note && (
                            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 rounded-md">
                                <p className="font-semibold">Important Note:</p>
                                <p className="text-sm">{pkg.note}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-10 text-center space-y-4">
                    <button onClick={() => navigate('register')} className="bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-blue-800 transition-colors duration-300 transform hover:scale-105">
                        Register for ThessMUN Now!
                    </button>
                    <button onClick={() => navigate('pricing')} className="block mx-auto text-blue-600 hover:underline font-medium text-sm mt-4">
                        Back to All Packages
                    </button>
                </div>
            </div>
        </section>
    );
};

export { PricingPage, PackageDetailPage };
